import {measureTextWidth} from './textMeasure';

// Medição real de pixel (canvas), não char×10px -- esse heurístico (ainda usado em
// helpers/report.js:getColumnWidth pros 7 relatórios, fora de escopo aqui) superestima
// caracteres estreitos (espaço, ':', '-', dígitos) e sobra espaço livre. Fonte/peso batem com o
// que a célula/cabeçalho de DataTable e sinister-table de fato herdam do Bootstrap (body: 1rem
// 400; cabeçalho: bold só em DataTable, mas medir sempre em bold aqui é seguro -- nunca estreita
// menos que o texto real, só evita medir menos que o necessário).
const CELL_FONT_STACK = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const CELL_FONT = `400 16px ${CELL_FONT_STACK}`;
const HEADER_FONT = `700 16px ${CELL_FONT_STACK}`;

// Folga de padding/borda da célula -- sem isso o texto mais longo encosta na borda por 1-2px e
// ainda corta, o que anularia o cálculo.
const PADDING_PX = 40;

/**
 * PERFORMANCE: quantos textos distintos de uma coluna entram na medição de largura. Medir a lista
 * inteira custa por linha (formatação de data, rótulo i18n, canvas) e passou de 0,1 ms por linha
 * nas telas de Sinistro e Dispositivo.
 */
export const WIDTH_SAMPLE_SIZE = 50;

/**
 * Escolhe, entre os textos distintos de uma coluna, os WIDTH_SAMPLE_SIZE mais longos em número de
 * caracteres e só então os formata. Coluna com poucos valores distintos (sim/não, status) entra
 * inteira, então o resultado é exato. Em coluna de texto livre o mais largo em pixel pode ser um
 * texto mais curto em caracteres, por exemplo "WWWW" contra "iiiiiiii".
 *
 * ATALHO: o texto mais longo em caracteres representa o mais largo em pixel; em coluna de data
 * bruta todos os textos têm o mesmo tamanho e entram os primeiros distintos, o que a folga de
 * padding cobre. Se uma coluna cortar texto, aumentar WIDTH_SAMPLE_SIZE ou voltar a passar `values` com a lista inteira.
 *
 * @param {Array<object>} rows linhas da tabela
 * @param {Function} pick texto barato de cada linha, usado para escolher a amostra
 * @param {Function} [format] formata só o que foi escolhido (data, rótulo i18n)
 * @param {number} [size] tamanho da amostra
 * @return {Array<*>} textos prontos para medir
 */
export const sampleWidest = (rows, pick, format = (value) => value, size = WIDTH_SAMPLE_SIZE) => {
  // O texto escolhe a amostra, mas o formatador recebe o valor original: número (epoch) virado em
  // texto deixa de ser data.
  const distinct = new Map();
  rows.forEach((row) => {
    const value = pick(row);
    const text = value === null || value === undefined ? '' : String(value);
    if (!distinct.has(text)) distinct.set(text, value === null || value === undefined ? '' : value);
  });
  if (distinct.size <= size) return [...distinct.values()].map((value) => format(value));
  const byLength = new Map();
  distinct.forEach((value, text) => {
    const bucket = byLength.get(text.length);
    if (bucket) bucket.push(value);
    else byLength.set(text.length, [value]);
  });
  const chosen = [];
  [...byLength.keys()].sort((a, b) => b - a).some((length) => {
    for (const value of byLength.get(length)) {
      chosen.push(value);
      if (chosen.length === size) return true;
    }
    return false;
  });
  return chosen.map((value) => format(value));
};

/**
 * width = max(largura do título, largura do maior conteúdo da coluna), nunca menor que o título
 * e nunca menor que o conteúdo (não corta texto). Cada coluna traz `values` (todos os textos) ou
 * `rows` com `pick` e `format` opcional (amostra dos mais longos, ver sampleWidest).
 *
 * `maxWidthRatio`, quando informado, capa a largura em `título × maxWidthRatio` -- usado só na
 * exceção da coluna de alarme em Notificação: a coluna nunca expande além de
 * título+50% (`maxWidthRatio: 1.5`), mesmo que o conteúdo seja mais longo; quando o conteúdo cabe
 * dentro do teto, o teto não entra em jogo (o max() já resolve sozinho).
 *
 * `extraPaddingPx`, quando informado, soma ao padding base -- usado quando o cabeçalho carrega
 * mais que o texto do título (ex. ícone de ordenação clicável), pra esse elemento não ficar
 * espremido numa coluna cujo conteúdo é só um pouco maior que o título.
 *
 * @param {Array<object>} columns colunas com `key`, `title`, `values` ou `rows` com `pick` e `format`,
 * e opcionais `maxWidthRatio` e `extraPaddingPx`
 * @return {Record<string, number>} largura em px por `key`
 */
export const computeColumnWidths = (columns) => {
  const widths = {};
  columns.forEach(({key, title, values: allValues, rows, pick, format, maxWidthRatio, extraPaddingPx = 0}) => {
    const values = rows ? sampleWidest(rows, pick, format) : allValues;
    const titleWidth = Math.ceil(measureTextWidth(title ? String(title) : '', HEADER_FONT)) + PADDING_PX + extraPaddingPx;
    const longestValuePx = values.reduce((max, value) => {
      const text = value === null || value === undefined ? '' : String(value);
      return Math.max(max, measureTextWidth(text, CELL_FONT));
    }, 0);
    const contentWidth = Math.ceil(longestValuePx) + PADDING_PX;
    let width = Math.max(titleWidth, contentWidth);
    if (maxWidthRatio) {
      width = Math.min(width, Math.round(titleWidth * maxWidthRatio));
    }
    widths[key] = width;
  });
  return widths;
};

/**
 * Converte px absolutos em % proporcional ao conteúdo de cada coluna -- pra quando a tabela
 * precisa preencher 100% de um container que não pode encolher (ex. o card da tela não diminui),
 * e a diferença de conteúdo entre colunas vale como fatia de 100% em vez de px fixo. Duas colunas, uma com o dobro do
 * conteúdo da outra, ficam com o dobro do espaço, e não sobra faixa vazia no card.
 *
 * @param {Record<string, number>} widthsPx saída de computeColumnWidths
 * @return {Record<string, string>} `"NN.N%"` por chave, somando 100% (a menos de arredondamento)
 */
export const toProportionalPercentages = (widthsPx) => {
  const total = Object.values(widthsPx).reduce((sum, px) => sum + px, 0);
  const percentages = {};
  Object.entries(widthsPx).forEach(([key, px]) => {
    percentages[key] = total > 0 ? `${(px / total * 100).toFixed(1)}%` : '0%';
  });
  return percentages;
};
